import queries from "@/queries";
import { useQuery } from "@tanstack/react-query";
import { DsdFile, DsdFileRaw, DsdFileStatus } from "@/types/dsd";

export const addDisplayLabel = (files: DsdFileRaw[]): DsdFile[] => {
  const originalNameCount = files.reduce(
    (acc, file) => {
      acc[file.name] = (acc[file.name] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const displayNameCount = files.reduce(
    (acc, file) => {
      if (file.displayName) {
        acc[file.displayName] = (acc[file.displayName] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  return files.map((file) => {
    if (file.displayName) {
      if (displayNameCount[file.displayName] <= 1) {
        return {
          ...file,
          label: file.displayName,
        };
      }
      return {
        ...file,
        label: `${file.displayName} (${file.revision})`,
      };
    }
    // If no displayName, check for duplicates among original names
    if (originalNameCount[file.name] <= 1) {
      return {
        ...file,
        label: file.name,
      };
    }
    return {
      ...file,
      label: `${file.name} (${file.revision})`,
    };
  });
};

const getStatusesToInclude = (status: DsdFileStatus): DsdFileStatus[] => {
  switch (status) {
    case DsdFileStatus.DSD_ANALYZED:
      return [DsdFileStatus.DSD_ANALYZED, DsdFileStatus.XBRL_ANALYZED];
    case DsdFileStatus.XBRL_ANALYZED:
      return [DsdFileStatus.XBRL_ANALYZED];
  }
};

export const useDSDFiles = ({
  select,
  enabled = true,
  status = DsdFileStatus.XBRL_ANALYZED,
}: {
  select?: (data: DsdFileRaw[]) => DsdFile[];
  enabled?: boolean;
  status?: DsdFileStatus;
} = {}) => {
  return useQuery({
    ...queries.dsd(),
    select: (data) => {
      const allowedStatuses = getStatusesToInclude(status);
      const filteredData = data.filter((file) =>
        allowedStatuses.includes(file.status),
      );
      if (select) {
        return select(filteredData);
      }

      return addDisplayLabel(filteredData).sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    },
    enabled,
  });
};
